import { List, fromJS } from 'immutable'
import type { ReactElement } from 'react'
import { defaults } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import cc from 'classcat'

import type { BlockAnimation } from './BlockAnimationSettings'
import { DragSource } from './utils/DragAndDrop'
import { blockSupportsHeadline, isHeadlineHidden } from './Workspace.helpers'
import { blockTypes } from './blocks'
import { emptyHeadline } from '../../../components/editorSidebarBlockTemplates'
import { getPlain } from '../../../store/utils'
import { resolveColorValue } from '../../../utils/color-refs'
import { themeGroupDelimiter } from '../../../../utils/themes'
import { useTranslate } from '../../../utils/translate'
import BlockAnimationSettings from './BlockAnimationSettings'
import BlockBackgroundSettings from './BlockBackgroundSettings'

export type BackgroundColorSettings = {
  // Each color field pairs a custom color with a palette ref; the custom
  // color takes precedence, and each writer nulls the other field.
  backgroundColor: string | null
  backgroundColorRef: string | null
  backgroundImage: {
    src: string
    width: number | null
    height: number | null
  }
  backgroundImageAlignment: 'top' | 'center' | 'bottom'
  backgroundType: 'color' | 'gradient' | 'image'
  gradientStartColor: string | null
  gradientStartColorRef: string | null
  gradientMidColor: string | null
  gradientMidColorRef: string | null
  gradientEndColor: string | null
  gradientEndColorRef: string | null
  gradientOrientation: '45deg' | '90deg' | '180deg'
  showGradientMidColor: boolean
  backgroundOverlayOpacity: number
}

export const defaultBackgroundColorSettings = {
  backgroundColor: 'transparent',
  backgroundColorRef: null,
  backgroundImage: {
    src: '',
    width: null,
    height: null,
  },
  backgroundImageAlignment: 'center',
  backgroundType: 'color',
  gradientStartColor: '#ffffff',
  gradientStartColorRef: null,
  gradientMidColor: '#828282',
  gradientMidColorRef: null,
  gradientEndColor: '#000000',
  gradientEndColorRef: null,
  gradientOrientation: '90deg',
  showGradientMidColor: false,

  backgroundOverlayOpacity: 20,
} satisfies BackgroundColorSettings

export type BackgroundWidthSettings = {
  width: 'content' | 'full'
  isUserModified?: boolean
}

export const getDefaultBackgroundWidthSettings = (themeName: string): BackgroundWidthSettings => {
  const [themeNamespace] = themeName.split(themeGroupDelimiter)

  switch (themeNamespace) {
    case 'epages.dimension':
    case 'epages.neutral':
    case 'epages.essence':
    case 'epages.uptown':
    case 'epages.structure':
      return { width: 'content' }
    case 'epages.vision':
    case 'epages.limelight':
    case 'epages.editorial':
      return { width: 'full' }
    default:
      return { width: 'content' }
  }
}

const alignmentButtonAllowedTypes = [
  'epages.text',
  'epages.image',
  'epages.video',
  'epages.product',
  'epages.contactform',
]

const singleColumnBlockTypes = [
  'epages.text',
  'epages.image',
  'epages.video',
  'epages.product',
  'epages.contactform',
  'epages.productslider',
  'epages.imagegallery',
]

function showAlignmentButton(blockType: string): boolean {
  if (singleColumnBlockTypes.includes(blockType)) {
    return false
  }
  return alignmentButtonAllowedTypes.some((allowedType) => blockType.includes(allowedType))
}

function updateProductSliderIds(block: ImmutableMap): ImmutableMap {
  const columns = block.getIn(['data', 'columns'])
  if (!columns) return block
  const updatedColumns = columns.map((column: ImmutableMap) => {
    if (column.get('type') === 'epages.productslider' && column.hasIn(['data', 'id'])) {
      return column.setIn(['data', 'id'], uuidv4())
    }
    return column
  })
  return block.setIn(['data', 'columns'], updatedColumns)
}

type Props = {
  block: ImmutableMap
  blockPosition: number
  isDragActive: boolean
  config: any
  editorView: boolean
  onChange: (block: ImmutableMap) => void
  onDelete: () => void
  onDuplicate: (block: ImmutableMap, newPosition: number) => void
}

export default function Block({
  block,
  blockPosition,
  isDragActive,
  config,
  editorView,
  onChange,
  onDelete,
  onDuplicate,
}: Readonly<Props>): ReactElement {
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const t = useTranslate('interface', 'components.elementContextBarComponent')
  const themeName = useSelector<State, string>((state) => state.getIn(['shop', 'theme', 'name']))

  const backgroundColorSettings = block.get('backgroundColorSettings')
  const legacyBackgroundColorSetting = block.get('backgroundColor')

  const initialBackgroundColorSettings = useMemo(() => {
    const storedSettings = getPlain(backgroundColorSettings) as
      | (Partial<BackgroundColorSettings> & { colorOrGradient?: 'color' | 'gradient' })
      | undefined
    // Migrate legacy `colorOrGradient` field to the new `backgroundType` field
    const migratedSettings = storedSettings
      ? {
          ...storedSettings,
          backgroundType: storedSettings.backgroundType ?? storedSettings.colorOrGradient,
        }
      : undefined
    return defaults(
      {},
      migratedSettings,
      { backgroundColor: legacyBackgroundColorSetting },
      defaultBackgroundColorSettings,
    )
  }, [backgroundColorSettings, legacyBackgroundColorSetting])

  const [currentBackgroundColorSettings, setCurrentBackgroundColorSettings] =
    useState<BackgroundColorSettings>(initialBackgroundColorSettings)

  // Re-sync when a color deletion remap rewrites the stored refs while this
  // component is mounted. During layer editing the block prop does not
  // change, so this sync never overwrites open edits.
  useEffect(() => {
    setCurrentBackgroundColorSettings(initialBackgroundColorSettings)
  }, [initialBackgroundColorSettings])

  const backgroundWidthSettings = block.get('backgroundWidthSettings')

  const initialBackgroundWidthSettings = useMemo(() => {
    const storedSettings = getPlain(backgroundWidthSettings) || {}
    const themeDefaults = getDefaultBackgroundWidthSettings(themeName)

    // If user has never modified the setting, or if it's not marked as user-modified,
    // use the theme default
    if (!storedSettings.isUserModified) {
      return {
        ...themeDefaults,
        isUserModified: false,
      }
    }

    // If user has modified it, keep their setting
    return defaults({}, storedSettings, themeDefaults)
  }, [backgroundWidthSettings, themeName])

  const [currentBackgroundWidthSettings, setCurrentBackgroundWidthSettings] =
    useState<BackgroundWidthSettings>(initialBackgroundWidthSettings)

  // Update width settings when theme changes (only if not user-modified)
  useEffect(() => {
    if (!currentBackgroundWidthSettings.isUserModified) {
      const themeDefaults = getDefaultBackgroundWidthSettings(themeName)
      setCurrentBackgroundWidthSettings({
        ...themeDefaults,
        isUserModified: false,
      })
    }
  }, [themeName, currentBackgroundWidthSettings.isUserModified])

  const {
    backgroundColor,
    backgroundColorRef,
    backgroundImage,
    backgroundImageAlignment,
    gradientStartColor,
    gradientStartColorRef,
    gradientMidColor,
    gradientMidColorRef,
    gradientEndColor,
    gradientEndColorRef,
    gradientOrientation,
    backgroundType,
    showGradientMidColor,
    backgroundOverlayOpacity,
  } = currentBackgroundColorSettings
  const backgroundImageUrl = backgroundImage?.src
    ? config?.plugins?.['epages.image']?.config?.imageUrl?.(backgroundImage.src) || backgroundImage.src
    : ''

  const { width, isUserModified } = currentBackgroundWidthSettings

  const saveBackground = () => {
    const persistedColorSettings: BackgroundColorSettings = {
      backgroundColor,
      backgroundColorRef,
      backgroundImage,
      backgroundImageAlignment,
      gradientStartColor,
      gradientStartColorRef,
      gradientMidColor,
      gradientMidColorRef,
      gradientEndColor,
      gradientEndColorRef,
      gradientOrientation,
      backgroundType,
      showGradientMidColor,
      backgroundOverlayOpacity,
    }
    onChange(
      block.set('backgroundColorSettings', persistedColorSettings).set('backgroundWidthSettings', {
        width,
        isUserModified,
      }),
    )
  }

  const blockRef = useRef<HTMLElement>(undefined)
  const replayTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // On short viewports (e.g. iOS landscape) a -250px rootMargin can shrink the observed
  // root to zero height, so the block never registers as in view and stays invisible.
  const [isShortViewport, setIsShortViewport] = useState(false)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-height: 600px)')
    const listener = () => setIsShortViewport(mediaQuery.matches)
    mediaQuery.addEventListener('change', listener)
    listener()
    return () => mediaQuery.removeEventListener('change', listener)
  }, [])

  // `will-fade` is never removed, so a block that loses the animation class drops back to
  // opacity 0.001. Latch it: the observer restarts, reporting "not in view", on a rootMargin change.
  const [hasEnteredView, setHasEnteredView] = useState(false)
  const { ref: inViewRef } = useInView({
    skip: hasEnteredView,
    rootMargin: isShortViewport ? '0px 0px -100px 0px' : '0px 0px -250px 0px',
    onChange: (inView) => {
      if (inView) setHasEnteredView(true)
    },
  })

  // Use `useCallback` so we don't recreate the function on each render
  const setRefs = useCallback(
    (node) => {
      // Ref's from useRef needs to have the node assigned to `current`
      blockRef.current = node
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      inViewRef(node)
    },
    [inViewRef],
  )

  const columns = block.getIn(['data', 'columns']) || List()
  const numColumns: number = columns.size

  const [animation, setAnimation] = useState<BlockAnimation>(block.get('blockAnimation', 'none'))
  const [isAnimatingInEditor, setIsAnimatingInEditor] = useState(false)
  const handleAnimationChange = (animation: BlockAnimation) => {
    setAnimation(animation)
    return handleAnimationReplay()
  }
  const handleAnimationReplay = () =>
    new Promise<void>((resolve) => {
      if (blockRef.current) {
        setIsAnimatingInEditor(true)
        // Total = last element's delay + duration (0.6s), stagger delay is 0.15s per step.
        // Without headline: column 1 has no delay → last column at (numColumns - 1) * 0.15s.
        // With headline: column 1 shifts by one delay → last column at numColumns * 0.15s.
        const hasVisibleHeadline = hasHeadlineElement && !hasHiddenHeadline
        const totalMs = hasVisibleHeadline
          ? (0.6 + numColumns * 0.15) * 1000
          : (0.6 + Math.max(0, numColumns - 1) * 0.15) * 1000
        clearTimeout(replayTimerRef.current)
        replayTimerRef.current = setTimeout(() => {
          setIsAnimatingInEditor(false)
          resolve()
        }, totalMs)
      } else resolve()
    })
  const saveAnimationChange = () => onChange(block.set('blockAnimation', animation))

  const actionBarRef = useRef<HTMLDivElement>(null)

  // Build a string representation of the block structure. Examples:
  // epages.image
  // image+text-image+text-image+text
  const blockType: string = columns.reduce(
    (blockType: string, column: ImmutableMap) =>
      (blockType && blockType + '-') + (column.has('type') ? column.get('type') : column.keySeq().toJS().join('+')),
    '',
  )

  const BlockComponent = blockTypes[block.get('type')]
  if (!BlockComponent) return <div>{'Unknown block type "' + block.get('type') + '"'}</div>

  const hasHeadlineElement = blockSupportsHeadline(block.get('data'))
  const hasHiddenHeadline = isHeadlineHidden(block.get('data'))

  function handleHeadlineToggleButtonClick() {
    if (block.hasIn(['data', 'headline'])) {
      onChange(block.setIn(['data', 'headline', 'data', 'isHidden'], !hasHiddenHeadline))
    } else {
      // Existing shops do not have the "headline" property in the data.
      const initialHeadline = fromJS(emptyHeadline).setIn(['data', 'isHidden'], !hasHiddenHeadline)
      onChange(block.setIn(['data', 'headline'], initialHeadline))
    }
  }

  const isCenterAligned = block.getIn(['alignment'])

  function handleAlignmentToggleButtonClick() {
    if (block.hasIn(['alignment'])) {
      onChange(block.setIn(['alignment'], !isCenterAligned))
    } else {
      onChange(block.setIn(['alignment'], true))
    }
  }

  const handleDataChange = (data: unknown) => onChange(block.set('data', data))

  const handleBackgroundCancel = () => {
    setCurrentBackgroundColorSettings(initialBackgroundColorSettings)
    setCurrentBackgroundWidthSettings(initialBackgroundWidthSettings)
  }

  const handleWidthSettingsChange = (newSettings: BackgroundWidthSettings) => {
    setCurrentBackgroundWidthSettings(newSettings)
  }

  const handleDuplicate = () => {
    let newBlock = block.set('_id', uuidv4())

    // If it's a product slider specifically, ensure the ID is updated
    if (block.get('type') === 'multi-column') {
      newBlock = updateProductSliderIds(newBlock)
    }

    onDuplicate(newBlock, blockPosition + 1)
  }

  const resolveBlockColor = (
    field: 'backgroundColor' | 'gradientStartColor' | 'gradientMidColor' | 'gradientEndColor',
  ) =>
    resolveColorValue(
      currentBackgroundColorSettings[field],
      currentBackgroundColorSettings[`${field}Ref`],
      defaultBackgroundColorSettings[field],
    )

  const resolvedBackgroundColor = resolveBlockColor('backgroundColor')

  let backgroundImageStyle: string | undefined
  if (backgroundType === 'gradient') {
    const start = resolveBlockColor('gradientStartColor')
    const mid = resolveBlockColor('gradientMidColor')
    const end = resolveBlockColor('gradientEndColor')
    backgroundImageStyle = showGradientMidColor
      ? `linear-gradient( ${gradientOrientation}, ${start}, ${mid}, ${end})`
      : `linear-gradient( ${gradientOrientation}, ${start}, ${end})`
  } else if (backgroundType === 'image' && backgroundImageUrl) {
    backgroundImageStyle = `url("${backgroundImageUrl}")`
  }

  let backgroundPositionStyle: string | undefined
  if (backgroundType === 'image') {
    if (backgroundImageAlignment === 'top') {
      backgroundPositionStyle = 'center top'
    } else if (backgroundImageAlignment === 'bottom') {
      backgroundPositionStyle = 'center bottom'
    } else {
      backgroundPositionStyle = 'center center'
    }
  }

  return (
    <div
      id={`block-${block.get('_id')}`}
      data-testid={`block-${block.get('_id')}`}
      className={cc([
        'dali-block',
        {
          'block-background':
            resolvedBackgroundColor !== 'transparent' ||
            backgroundType === 'gradient' ||
            (backgroundType === 'image' && Boolean(backgroundImageUrl)),
          'drag-active': isDragActive,
          // Only animate when explicitly triggered (editor) / when in view (storefront)
          [animation]: animation !== 'none' && (editorView ? isAnimatingInEditor : hasEnteredView),
          'will-fade': animation !== 'none' && !editorView, // avoid flash of content before animation starts (storefront)
          'has-animation': animation !== 'none' && editorView, // for styling purposes (editor)
          'center-alignment': isCenterAligned,
          'background-width-content': width === 'content',
          'background-width-full': width === 'full',
        },
      ])}
      data-block-type={blockType}
      style={{
        backgroundColor: backgroundType === 'color' ? resolvedBackgroundColor : undefined,
        backgroundImage: backgroundImageStyle,
        backgroundPosition: backgroundPositionStyle,
        backgroundRepeat: backgroundType === 'image' ? 'no-repeat' : undefined,
        backgroundSize: backgroundType === 'image' ? 'cover' : undefined,
        backgroundOrigin: 'border-box',
        position: 'relative',
      }}
      ref={setRefs}
    >
      {editorView && (
        <div ref={actionBarRef} className="dali-block-actionbar">
          {!confirmingDelete && (
            <DragSource
              type="dali-add"
              className="dali-block-actionbar-button dali-block-actionbar-button-move"
              title={t('moveButton.label')}
              onDrag={() => block}
              blockPosition={blockPosition}
              createPreviewNode={() => {
                const { width = 10 } = actionBarRef.current?.getBoundingClientRect() || {}

                const div = document.createElement('div')
                div.className = 'dali-block-actionbar'
                div.style.width = `${width}px`
                div.style.visibility = 'visible'
                div.style.opacity = '1'

                const div2 = document.createElement('div')
                div2.className = 'dali-block-actionbar-button dali-block-actionbar-button-move'
                div.append(div2)

                return div
              }}
              previewNodeOffset={() => {
                const { width = 10, height = 10 } = actionBarRef.current?.getBoundingClientRect() || {}

                return { x: width / 2, y: height / 2 }
              }}
            />
          )}
          {confirmingDelete && (
            <>
              <span>{t('deleteButton.warningMessage')}</span>
              <div className="dali-block-actionbar-button-separator"></div>
              <button
                className="dali-block-actionbar-button dali-block-actionbar-button-delete-confirm"
                onClick={onDelete}
              >
                <span>{t('deleteConfirmButton.label')}</span>
              </button>
              <div className="dali-block-actionbar-button-separator"></div>
              <button
                className="dali-block-actionbar-button dali-block-actionbar-button-delete-cancel"
                onClick={() => setConfirmingDelete(false)}
              >
                <span>{t('deleteDismissButton.label')}</span>
              </button>
            </>
          )}
          {!confirmingDelete && <div className="dali-block-actionbar-button-separator"></div>}
          {!confirmingDelete && (
            <BlockBackgroundSettings
              currentColorSettings={currentBackgroundColorSettings}
              currentWidthSettings={currentBackgroundWidthSettings}
              config={config}
              onColorSettingsChange={setCurrentBackgroundColorSettings}
              onWidthSettingsChange={handleWidthSettingsChange}
              onCancel={handleBackgroundCancel}
              onSave={saveBackground}
              t={t}
              themeName={themeName}
            />
          )}

          {!confirmingDelete && (
            <BlockAnimationSettings
              animation={animation}
              initialAnimation={block.get('blockAnimation', 'none')}
              isAnimating={isAnimatingInEditor}
              handleAnimationChange={handleAnimationChange}
              handleAnimationReplay={handleAnimationReplay}
              saveAnimationChange={saveAnimationChange}
              setAnimation={setAnimation}
              t={t}
            />
          )}
          {!confirmingDelete && hasHeadlineElement && (
            <button
              className={cc({
                'dali-block-actionbar-button dali-block-actionbar-button-headline': true,
                'dali-block-actionbar-button-headline-active': !hasHiddenHeadline,
              })}
              onClick={handleHeadlineToggleButtonClick}
              title={t(hasHiddenHeadline ? 'showHeadlineButton.label' : 'hideHeadlineButton.label')}
            />
          )}
          {!confirmingDelete && showAlignmentButton(blockType) && (
            <button
              className={cc({
                'dali-block-actionbar-button dali-block-actionbar-button-alignment': true,
                'dali-block-actionbar-button-alignment-active': isCenterAligned,
              })}
              onClick={handleAlignmentToggleButtonClick}
              title={t(isCenterAligned ? 'startAlignmentButton.label' : 'centerAlignmentButton.label')}
            />
          )}
          {!confirmingDelete && blockType !== 'ng.category' && (
            <button
              className="dali-block-actionbar-button dali-block-actionbar-button-duplicate"
              title={t('duplicateButton.label')}
              onClick={handleDuplicate}
            />
          )}
          {!confirmingDelete && (
            <button
              className="dali-block-actionbar-button dali-block-actionbar-button-delete"
              title={t('deleteButton.label')}
              onClick={() => setConfirmingDelete(true)}
            />
          )}
        </div>
      )}
      {backgroundType === 'image' && !!backgroundImageUrl && backgroundOverlayOpacity > 0 && (
        <div
          aria-hidden
          data-testid="block-background-overlay"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'black',
            opacity: backgroundOverlayOpacity / 100,
            pointerEvents: 'none',
          }}
        />
      )}
      {editorView && <span className="dali-block-selection-border" aria-hidden="true" />}
      <BlockComponent
        config={config}
        editorView={editorView}
        data={block.get('data')}
        onDataChange={handleDataChange}
        settings={block.get('settings')}
      />
    </div>
  )
}
